import { useState, useCallback, useEffect } from "react";
import { fetch } from "./api-wrapper";
import { IMPUTATION_FETCH_PAGE_SIZE } from "./expenses-imputations-config-helpers";
const DIRECTORY_SORTING = encodeURIComponent(JSON.stringify([{ id: "category", desc: false }]));

/**
 * Loads the full imputation directory and tracks which imputation IDs have
 * children (i.e. are parents). Used by the imputations page to resolve whether
 * a clicked row should open in parent-edit vs. leaf-edit mode.
 *
 * @param {string|null} [imputationCatalog] - When provided ("expense" | "displacement"),
 *   scopes the directory fetch to that catalog. Omit, pass null, or pass an empty
 *   string for an unfiltered load (all three are treated identically).
 * @returns {{ parentIds: Set<string>, loadImputationDirectory: () => void }}
 */
export function useImputationDirectory(imputationCatalog = null) {
  const [parentIds, setParentIds] = useState(new Set());

  const loadImputationDirectory = useCallback(() => {
    const pIds = new Set();
    // Fixed catalog filter when the caller scopes this hook to a specific catalog.
    const baseFilters = imputationCatalog
      ? [{ id: "imputation_catalog", value: imputationCatalog }]
      : [];
    const fetchPage = (start) => {
      const searchData = `?filters=${encodeURIComponent(JSON.stringify(baseFilters))}&sorting=${DIRECTORY_SORTING}&start=${start}&page_size=${IMPUTATION_FETCH_PAGE_SIZE}`;
      fetch(`/imputations/${searchData}`, "GET", {}, (response) => {
        const rows = response?.data?.imputations ?? [];
        rows.forEach((row) => {
          if (row.parent_id) pIds.add(String(row.parent_id));
        });
        const totalRowCount = response?.data?.meta?.totalRowCount ?? 0;
        if (rows.length > 0 && start + rows.length < totalRowCount) {
          fetchPage(start + rows.length);
        } else {
          setParentIds(pIds);
        }
      });
    };
    fetchPage(0);
  }, [imputationCatalog]);

  useEffect(() => {
    loadImputationDirectory();
  }, [loadImputationDirectory]);

  return { parentIds, loadImputationDirectory };
}
